/**
 * Mega Trainer Hub
 * Comprehensive sales training platform combining Lumo, SPIN, MEDDIC, Sandler, and Challenger methodologies
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { megaTrainerService } from '../../services/megaTrainerService';
import uploadProcessingService from '../../services/uploadProcessingService';
import NavigationBar from '../../components/ui/NavigationBar';
import PremiumGate from '../../components/PremiumGate';
import Icon from '../../components/AppIcon';
import LoadingState from '../../components/ui/LoadingState';

const MegaTrainer = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState([]);
  const [moduleProgress, setModuleProgress] = useState({});
  const [recommendation, setRecommendation] = useState(null);
  const [activeTab, setActiveTab] = useState('recommended'); // recommended, all, certified, framework, custom
  const [dailyBlock, setDailyBlock] = useState(null);
  const [dailyReflection, setDailyReflection] = useState('');
  const [customModules, setCustomModules] = useState([]);

  useEffect(() => {
    if (user?.id) {
      loadTrainerData();
    }
  }, [user?.id]);

  const loadTrainerData = async () => {
    try {
      setLoading(true);

      // Get recommended learning path
      const recommendedPath = await megaTrainerService.getRecommendedPath(user?.id);
      setModules(recommendedPath);

      // Get progress for all modules
      const progressMap = {};
      for (const module of Object.values(megaTrainerService.TRAINING_MODULES)) {
        const progress = await megaTrainerService.getModuleProgress(user?.id, module.id);
        progressMap[module.id] = progress;
      }
      setModuleProgress(progressMap);

      // Get adaptive recommendation
      const rec = await megaTrainerService.getAdaptiveRecommendation(user?.id);
      setRecommendation(rec);

      // Build a daily mega block (auto-assigned)
      const daily = buildDailyBlock(rec, progressMap, recommendedPath);
      setDailyBlock(daily);

      // Load custom training modules from uploaded materials
      if (profile?.primaryTeamId || profile?.primary_team_id) {
        const teamId = profile.primaryTeamId || profile.primary_team_id;
        const customMods = await uploadProcessingService.getCustomTrainingModules(teamId);
        setCustomModules(customMods);
      }
    } catch (error) {
      console.error('Error loading trainer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartModule = (moduleId) => {
    navigate(`/app/trainer/${moduleId}/lesson/1`);
  };

  const handlePracticeScenario = (moduleId) => {
    navigate(`/app/trainer/${moduleId}/scenarios`);
  };

  const handleDailyLesson = () => {
    if (!dailyBlock) return;
    navigate(`/app/trainer/${dailyBlock.moduleId}/lesson/${dailyBlock.lessonNumber}`);
  };

  const handleDailyScenario = () => {
    if (!dailyBlock) return;
    navigate(`/app/trainer/${dailyBlock.moduleId}/scenarios`, { state: { lessonNumber: dailyBlock.lessonNumber, daily: true } });
  };

  const handleDailyReroll = () => {
    if (!modules || modules.length === 0) {
      return;
    }
    const alt = buildDailyBlock(recommendation, moduleProgress, modules, dailyBlock?.moduleId);
    if (alt) setDailyBlock(alt);
  };

  const reflectionPrompts = [
    'What exact wording improved the objection turn-around?',
    'Where did you regain control without sounding pushy?',
    'What probe question opened the buyer up the most?',
    'What tone or pacing change shifted the call?',
    'What commitment step could you tighten next time?',
  ];

  const getReflectionPrompt = () => {
    const idx = Math.floor(Math.random() * reflectionPrompts.length);
    return reflectionPrompts[idx];
  };

  const buildDailyBlock = (rec, progressMap, recommendedPath, excludeModuleId = null) => {
    const candidateIds = [
      rec?.moduleId,
      ...(recommendedPath || []).map(m => m?.id).filter(Boolean),
      ...Object.keys(megaTrainerService.TRAINING_MODULES || {})
    ].filter(Boolean).filter((id, idx, arr) => arr.indexOf(id) === idx);

    const selectedId = candidateIds.find(id => id !== excludeModuleId && megaTrainerService.TRAINING_MODULES[id]) || candidateIds[0];
    const module = selectedId ? megaTrainerService.TRAINING_MODULES[selectedId] : null;
    if (!module) return null;

    const progress = progressMap?.[module.id];
    const completed = progress?.lessonsCompleted || 0;
    const nextLesson = Math.min((completed || 0) + 1, module.lessons || 1);
    const reflection = getReflectionPrompt();

    setDailyReflection(reflection);

    return {
      moduleId: module.id,
      moduleName: module.name,
      lessonNumber: nextLesson,
      difficulty: module.difficulty,
      duration: module.duration,
      reflection,
    };
  };

  if (loading) {
    return (
      <>
        <NavigationBar userStreak={0} />
        <div className="main-content bg-background">
          <LoadingState message="Loading your training programs..." />
        </div>
      </>
    );
  }

  const allModules = Object.values(megaTrainerService.TRAINING_MODULES);
  const certifiedModules = Object.keys(moduleProgress).filter(
    moduleId => moduleProgress[moduleId]?.certificateEarned
  );

  const displayModules = 
    activeTab === 'all' ? allModules :
    activeTab === 'certified' ? allModules.filter(m => certifiedModules.includes(m.id)) :
    modules;

  return (
    <>
      <Helmet>
        <title>Mega Trainer - Sales Mastery Hub</title>
        <meta name="description" content="Master Lumo, SPIN, MEDDIC, Sandler, and Challenger methodologies with adaptive learning paths and real-world scenarios." />
      </Helmet>
      <NavigationBar userStreak={0} />
      <PremiumGate feature="megaTrainer" requiredPlan="Pro">
        <div className="min-h-screen bg-gradient-to-br from-primary/5 to-secondary/5 pb-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

            {/* Hero Section */}
            <div className="mb-12">
              <div className="flex items-center gap-4 mb-6">
                <div className="p-3 bg-primary/10 rounded-xl border border-primary/20">
                  <Icon name="Zap" className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-foreground">Mega Sales Trainer</h1>
                  <p className="text-muted-foreground mt-1">Master the world's best sales methodologies in one adaptive platform</p>
                </div>
              </div>
            </div>

            {/* Daily Mega Block */}
            {dailyBlock && (
              <div className="mb-10 bg-card border border-border rounded-xl p-6 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="p-3 bg-primary/10 rounded-lg border border-primary/20">
                      <Icon name="Flame" className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">Daily Mega Block</p>
                      <h3 className="text-xl font-bold text-foreground mt-1">{dailyBlock.moduleName}</h3>
                      <p className="text-sm text-muted-foreground mt-1">Lesson {dailyBlock.lessonNumber} · {dailyBlock.difficulty} · {dailyBlock.duration}</p>
                      <div className="mt-3 text-sm text-foreground space-y-2">
                        <p className="font-semibold">Today’s pack:</p>
                        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                          <li>Lesson {dailyBlock.lessonNumber} (core concept)</li>
                          <li>Roleplay scenario with AI scoring</li>
                          <li>Reflection: {dailyReflection || dailyBlock.reflection}</li>
                          <li>XP boost for completing the pack</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 items-start">
                    <button
                      onClick={handleDailyLesson}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90"
                    >
                      <Icon name="Play" size={16} /> Start Guided Flow
                    </button>
                    <button
                      onClick={handleDailyScenario}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-secondary/20 text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/30"
                    >
                      <Icon name="MessageSquare" size={16} /> Jump to Roleplay
                    </button>
                    <button
                      onClick={handleDailyReroll}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg font-semibold hover:bg-muted/80"
                    >
                      <Icon name="RefreshCw" size={16} /> Reroll
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-2 mb-8 flex-wrap">
              {[
                { id: 'recommended', label: 'Recommended' },
                { id: 'all', label: 'All Modules' },
                { id: 'custom', label: `Custom Training${customModules.length > 0 ? ` (${customModules.length})` : ''}`, badge: customModules.length },
                { id: 'certified', label: 'Certified' },
                { id: 'framework', label: 'By Framework' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-1.5 rounded-md border text-sm relative ${
                    activeTab === tab.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-foreground border-border hover:bg-muted'
                  }`}
                >
                  {tab.label}
                  {tab.badge > 0 && activeTab !== tab.id && (
                    <span className="absolute -top-1 -right-1 bg-accent text-accent-foreground text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                      {tab.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Custom Training Tab */}
            {activeTab === 'custom' && (
              <div className="mb-10">
                {customModules.length === 0 ? (
                  <div className="bg-card border border-border rounded-xl p-12 text-center">
                    <Icon name="FileText" className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-xl font-semibold text-foreground mb-2">No Custom Training Yet</h3>
                    <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                      Upload your company's sales scripts and training materials to create custom training modules powered by Lumo AI.
                    </p>
                    <button
                      onClick={() => navigate('/app/upload-center')}
                      className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors inline-flex items-center gap-2"
                    >
                      <Icon name="Upload" className="w-5 h-5" />
                      Upload Training Materials
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h3 className="text-xl font-semibold text-foreground">Your Custom Training</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Modules auto-generated from your uploaded materials by Lumo AI
                        </p>
                      </div>
                      <button
                        onClick={() => navigate('/app/upload-center')}
                        className="px-4 py-2 bg-accent text-accent-foreground rounded-lg hover:bg-accent/90 transition-colors inline-flex items-center gap-2 text-sm"
                      >
                        <Icon name="Plus" className="w-4 h-4" />
                        Upload More
                      </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {customModules.map(module => (
                        <div
                          key={module.id}
                          className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-shadow cursor-pointer"
                          onClick={() => navigate(`/app/custom-training/${module.id}`)}
                        >
                          <div className="flex items-start gap-3 mb-4">
                            <div className="p-2 bg-accent/10 rounded-lg border border-accent/20">
                              <Icon name="Sparkles" className="w-6 h-6 text-accent" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-foreground mb-1">{module.title}</h4>
                              <span className="text-xs px-2 py-1 bg-accent/10 text-accent rounded">
                                Custom
                              </span>
                            </div>
                          </div>
                          
                          {module.description && (
                            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                              {module.description}
                            </p>
                          )}

                          {module.key_topics && module.key_topics.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                              {module.key_topics.slice(0, 3).map((topic, idx) => (
                                <span key={idx} className="text-xs px-2 py-1 bg-muted rounded">
                                  {topic}
                                </span>
                              ))}
                              {module.key_topics.length > 3 && (
                                <span className="text-xs px-2 py-1 bg-muted rounded">
                                  +{module.key_topics.length - 3} more
                                </span>
                              )}
                            </div>
                          )}

                          <div className="flex items-center justify-between text-sm">
                            <div className="flex items-center gap-4 text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Icon name="Clock" className="w-4 h-4" />
                                {module.estimated_duration || 15} min
                              </span>
                              <span className="flex items-center gap-1">
                                <Icon name="Target" className="w-4 h-4" />
                                {module.difficulty_level || 'intermediate'}
                              </span>
                            </div>
                          </div>

                          {module.sections && module.sections.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-border">
                              <p className="text-xs text-muted-foreground">
                                {module.sections.length} section{module.sections.length !== 1 ? 's' : ''}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'framework' && (
              <div className="mb-10 bg-card border border-border rounded-xl p-6">
                <h3 className="text-xl font-semibold text-foreground mb-2">Unified Framework</h3>
                <p className="text-sm text-muted-foreground mb-4">We combine SPIN, MEDDIC, Sandler, Challenger, NEPQ, GAP, Straight Line, and Value Selling into one cohesive system so you don’t feel like you’re learning different religions.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {megaTrainerService.UNIFIED_FRAMEWORK.phases.map(phase => (
                    <div key={phase.id} className="rounded-lg border border-border p-4 bg-background">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm uppercase tracking-wide text-muted-foreground">{phase.id}</span>
                        <Icon name="Sparkles" className="w-4 h-4 text-accent" />
                      </div>
                      <h4 className="font-semibold text-foreground mb-1">{phase.name}</h4>
                      <p className="text-sm text-muted-foreground mb-3">{phase.purpose}</p>
                      <div className="flex flex-wrap gap-2">
                        {phase.includes.slice(0, 6).map((inc, idx) => (
                          <button
                            key={idx}
                            onClick={() => navigate(`/app/trainer/${inc.module}/lesson/${megaTrainerService.getLessonIndexByPillar(inc.module, (inc.pillars && inc.pillars[0]) || megaTrainerService.TRAINING_MODULES?.[inc.module]?.pillars?.[0] || 1)}`)}
                            className="text-xs bg-muted text-foreground px-2 py-1 rounded hover:bg-muted/80"
                          >
                            {inc.module.replace('_', ' ')}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Adaptive Recommendation Card */}
            {recommendation && (
              <div className="mb-8 bg-gradient-to-r from-accent/10 to-primary/10 rounded-xl p-6 border border-accent/20">
                <div className="flex items-start gap-4">
                  <Icon name="Lightbulb" className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {recommendation.recommendation}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">{recommendation.reason}</p>
                    {recommendation.improvement && (
                      <p className="text-xs text-accent font-medium mb-4">{recommendation.improvement}</p>
                    )}
                    <button
                      onClick={() => handleStartModule(recommendation.moduleId)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-lg font-medium transition-all"
                    >
                      <Icon name="ArrowRight" size={16} />
                      Start Learning
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="flex gap-2 mb-8 border-b border-border">
              <button
                onClick={() => setActiveTab('recommended')}
                className={`px-4 py-3 font-medium border-b-2 transition-all ${
                  activeTab === 'recommended'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Recommended Path
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`px-4 py-3 font-medium border-b-2 transition-all ${
                  activeTab === 'all'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                All Programs
              </button>
              <button
                onClick={() => setActiveTab('certified')}
                className={`px-4 py-3 font-medium border-b-2 transition-all ${
                  activeTab === 'certified'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                Certifications ({certifiedModules.length})
              </button>
            </div>

            {/* Training Modules Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayModules.map(module => {
                const progress = moduleProgress[module.id];
                const isStarted = progress && progress.lessonsCompleted > 0;
                const isCertified = progress?.certificateEarned;
                const progressPercent = progress?.progressPercent || 0;

                return (
                  <div
                    key={module.id}
                    className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-elevated hover:border-primary/30 transition-all duration-200"
                  >
                    {/* Module Header */}
                    <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="text-lg font-bold text-foreground">{module.name}</h3>
                          <p className="text-xs text-muted-foreground mt-1 font-medium">{module.difficulty}</p>
                        </div>
                        {isCertified && (
                          <div className="bg-success/20 p-2 rounded-full">
                            <Icon name="CheckCircle" className="w-5 h-5 text-success" />
                          </div>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-4">{module.description}</p>

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Icon name="Clock" size={14} />
                          <span>{module.duration}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Icon name="BookOpen" size={14} />
                          <span>{module.lessons} lessons</span>
                        </div>
                      </div>
                    </div>

                    {/* Progress Section */}
                    <div className="p-6 border-t border-border">
                      {isStarted ? (
                        <>
                          <div className="mb-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-foreground">Progress</span>
                              <span className="text-sm font-bold text-primary">{progressPercent}%</span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mb-4">
                            {progress?.lessonsCompleted} of {progress?.lessonsTotal} lessons completed
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground mb-4">Not started yet</p>
                      )}

                      {/* Pillars Tags */}
                      <div className="flex flex-wrap gap-2 mb-6">
                        {module.pillars.slice(0, 2).map(pillar => (
                          <span
                            key={pillar}
                            className="text-xs px-2.5 py-1 bg-primary/10 text-primary rounded-full font-medium"
                          >
                            {pillar.replace(/_/g, ' ')}
                          </span>
                        ))}
                        {module.pillars.length > 2 && (
                          <span className="text-xs px-2.5 py-1 bg-muted text-muted-foreground rounded-full font-medium">
                            +{module.pillars.length - 2} more
                          </span>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStartModule(module.id)}
                          className="flex-1 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-all text-sm"
                        >
                          {isStarted ? 'Continue' : 'Start'}
                        </button>
                        <button
                          onClick={() => handlePracticeScenario(module.id)}
                          className="flex-1 px-4 py-2 bg-card border border-border hover:bg-muted text-foreground rounded-lg font-medium transition-all text-sm"
                        >
                          Practice
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {displayModules.length === 0 && (
              <div className="bg-card rounded-xl p-12 border-2 border-border text-center">
                <Icon name="BookOpen" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No programs available</h3>
                <p className="text-muted-foreground">
                  {activeTab === 'certified' ? 'Complete modules to earn certifications' : 'No training programs found'}
                </p>
              </div>
            )}

            {/* Success Tips Section */}
            <div className="mt-16 bg-gradient-to-r from-secondary/10 to-accent/10 rounded-xl p-8 border border-secondary/20">
              <h2 className="text-2xl font-bold text-foreground mb-6">Training Tips for Success</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-secondary/20 rounded-lg">
                      <Icon name="Target" size={20} className="text-secondary" />
                    </div>
                    <h3 className="font-semibold text-foreground">Daily Practice</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Spend 10-15 minutes daily on lessons and scenarios. Consistency beats intensity.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-accent/20 rounded-lg">
                      <Icon name="MessageSquare" size={20} className="text-accent" />
                    </div>
                    <h3 className="font-semibold text-foreground">Apply Immediately</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Use techniques in real calls and practice scenarios. Transfer learning requires application.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-primary/20 rounded-lg">
                      <Icon name="TrendingUp" size={20} className="text-primary" />
                    </div>
                    <h3 className="font-semibold text-foreground">Track Progress</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Monitor your improvements and unlock certifications. Every small win counts!
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </PremiumGate>
    </>
  );
};

export default MegaTrainer;
